import { clientPushService } from "@/services/push.service/clientPush.api";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

export type PushSupportState = "unsupported" | "unsubscribed" | "subscribed" | "denied";

/** Converte a chave pública VAPID (base64url) para o formato que a Push API espera. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

const isSupported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export function useClientPushNotifications() {
  const [state, setState] = useState<PushSupportState>("unsubscribed");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!isSupported()) {
      setState("unsupported");
      setLoading(false);
      return;
    }
    if (Notification.permission === "denied") {
      setState("denied");
      setLoading(false);
      return;
    }
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    setState(existing ? "subscribed" : "unsubscribed");
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const subscribe = useCallback(async () => {
    if (!isSupported()) {
      toast.error("Seu navegador não suporta notificações push");
      return;
    }
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState(permission === "denied" ? "denied" : "unsubscribed");
        return;
      }

      const [vapid, vapidErr] = await clientPushService.getVapidPublicKey();
      if (vapidErr || !vapid?.publicKey) {
        toast.error("Não foi possível configurar as notificações push agora");
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.publicKey),
      });

      const json = subscription.toJSON();
      const [, err] = await clientPushService.subscribe({
        endpoint: json.endpoint!,
        keys: { p256dh: json.keys!.p256dh, auth: json.keys!.auth },
      });
      if (err) {
        toast.error("Erro ao registrar notificações push");
        return;
      }

      setState("subscribed");
      toast.success("Notificações push ativadas!");
    } finally {
      setLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await clientPushService.unsubscribe(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setState("unsubscribed");
      toast.success("Notificações push desativadas");
    } finally {
      setLoading(false);
    }
  }, []);

  return { state, loading, subscribe, unsubscribe };
}
